import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { extractExternalVideoEmbedUrl } from '../../propertyCard/propertyCardUtils';
import { headingNotSet } from '../../../services/TemplateUtils';
import type { CarouselItemContent } from '../../template/imageCarouselTypes';
import type { CardVideoUploadPhase } from './useCardVideoUpload';

export type VideoZoneView =
  | 'uploading'
  | 'transcoding'
  | 'failed'
  | 'library-ready'
  | 'library-loading'
  | 'embed'
  | 'drop-hint';

function isLibraryItem(item: CarouselItemContent): boolean {
  return item.videoSource === 'library' && Boolean(item.libraryVideoExternalId);
}

function isTranscoding(phase: CardVideoUploadPhase, asset: VideoAsset | null): boolean {
  return phase === 'transcoding' || asset?.status === 'PENDING' || asset?.status === 'PROCESSING';
}

function isFailed(phase: CardVideoUploadPhase, asset: VideoAsset | null): boolean {
  return phase === 'error' || asset?.status === 'FAILED';
}

function isLibraryReady(item: CarouselItemContent, asset: VideoAsset | null): boolean {
  return isLibraryItem(item) && asset?.status === 'READY';
}

function isLibraryLoading(item: CarouselItemContent, asset: VideoAsset | null): boolean {
  return isLibraryItem(item) && !asset;
}

function isEmbeddable(item: CarouselItemContent, embedUrl: string): boolean {
  return Boolean(embedUrl) && item.videoSource !== 'library';
}

export function failedZoneMessage(errorMessage: string | null, asset: VideoAsset | null): string {
  return errorMessage || asset?.errorMessage || 'Processing failed';
}

export function computeEmbedUrl(item: CarouselItemContent): string {
  return item.videoURL?.url ? extractExternalVideoEmbedUrl(item.videoURL.url) : '';
}

export function libraryPlayerCardTitle(item: CarouselItemContent): string | undefined {
  return headingNotSet(item.heading) ? undefined : item.heading.content.trim();
}

/** Which of the slide video zone's mutually-exclusive views applies, in priority order. */
export function resolveVideoZoneView(
  phase: CardVideoUploadPhase,
  asset: VideoAsset | null,
  item: CarouselItemContent,
  embedUrl: string,
): VideoZoneView {
  if (phase === 'uploading') return 'uploading';
  if (isTranscoding(phase, asset)) return 'transcoding';
  if (isFailed(phase, asset)) return 'failed';
  if (isLibraryReady(item, asset)) return 'library-ready';
  if (isLibraryLoading(item, asset)) return 'library-loading';
  if (isEmbeddable(item, embedUrl)) return 'embed';
  return 'drop-hint';
}
